import React, {useEffect, useState} from "react";
import * as axios from "axios";
import {
  Dialog, IconButton, Button, TextField, InputAdornment, Menu, MenuItem,
  Switch, FormControlLabel,
} from "@material-ui/core";
import CloseIcon from "@material-ui/icons/Close";
import Visibility from "@material-ui/icons/Visibility";
import VisibilityOff from "@material-ui/icons/VisibilityOff";
import HistoryIcon from "@material-ui/icons/History";
import {formatDate} from "./shiftUtils";
import "./styles.css";

const baseUrl = 'https://api.appetito.ro/';

const emptyForm = {
  username: '', password: '', phone: '', firstName: '', lastName: '',
  platitPeComanda: false, sumaComanda: '', cityId: '',
};

function formFromDeliverer(deliverer) {
  if (!deliverer) {
    return emptyForm;
  }
  return {
    username: deliverer.username || '',
    password: deliverer.password || '',
    phone: deliverer.phone || '',
    firstName: deliverer.first_name || '',
    lastName: deliverer.last_name || '',
    platitPeComanda: !!deliverer.platit_pe_comanda,
    sumaComanda: deliverer.suma_comanda ? String(deliverer.suma_comanda) : '',
    cityId: deliverer.city_id || '',
  };
}

// deliverer === null means "create new"; an object means "edit this one".
// Personal.js keys this component on `${deliverer.id}-${open}` — a fresh
// instance every time the dialog opens, so form/error/showPassword can
// safely init straight from props here instead of via an effect (see
// CarModal's identical fix and the comment on its own key prop for why:
// syncing local state from a prop via useEffect on a long-lived instance
// left a window where the previous deliverer's data could still be showing
// right after opening a different one).
//
// isSuperAdmin gates the "Salariu de bază" field entirely — Deliverer.
// BaseSalary is hidden (json:"-") from the regular deliverer list every
// admin can fetch, so a non-super-admin never even has the value to show,
// and the backend independently ignores base_salary on save from anyone
// else (see UpdateDeliverer's canEditSalary check) — this prop only
// controls whether the field renders at all.
export default function DelivererModal({open, deliverer, onClose, onSaved, isSuperAdmin}) {
  const isEditing = !!deliverer;
  const [form, setForm] = useState(() => formFromDeliverer(deliverer));
  const [showPassword, setShowPassword] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [baseSalary, setBaseSalary] = useState('');
  const [baseSalaryHistory, setBaseSalaryHistory] = useState([]);
  const [salaryHistoryAnchor, setSalaryHistoryAnchor] = useState(null);
  const [cities, setCities] = useState([]);

  // Main cities only (same list the sidebar's city dropdown uses) — a
  // deliverer belongs to one main city, same as Restaurant.city_id.
  useEffect(() => {
    axios.default.get(`${baseUrl}v1/city`).then((response) => {
      setCities(response.data || []);
    }).catch(() => setCities([]));
  }, []);

  useEffect(() => {
    if (open && isEditing && isSuperAdmin) {
      const token = localStorage.getItem('access_token');
      axios.default.get(`${baseUrl}v1/admin/deliverer/${deliverer.id}/base-salary`, {
        headers: {Authorization: `Bearer ${token}`},
      }).then((response) => {
        setBaseSalary(String(response.data.base_salary || ''));
        setBaseSalaryHistory(response.data.history || []);
      }).catch(() => {
        // Not super admin (shouldn't happen, the field wouldn't be shown) or
        // a transient error — leave the field blank rather than fail the
        // whole modal open.
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, isEditing]);

  const setField = (field) => (event) => setForm({...form, [field]: event.target.value});

  const save = async () => {
    if (!form.username || (!isEditing && !form.password)) {
      setError('Username și parolă sunt obligatorii.');
      return;
    }
    if (!form.cityId) {
      setError('Orașul este obligatoriu.');
      return;
    }

    setSaving(true);
    setError('');
    try {
      const token = localStorage.getItem('access_token');
      const payload = {
        username: form.username,
        password: form.password,
        phone: form.phone,
        first_name: form.firstName,
        last_name: form.lastName,
        platit_pe_comanda: form.platitPeComanda,
        suma_comanda: form.sumaComanda === '' ? 0 : parseFloat(form.sumaComanda),
        city_id: parseInt(form.cityId, 10),
      };
      if (isSuperAdmin) {
        payload.base_salary = baseSalary === '' ? 0 : parseFloat(baseSalary);
      }
      const response = isEditing
        ? await axios.default.put(`${baseUrl}v1/admin/deliverer/${deliverer.id}`, payload, {
          headers: {Authorization: `Bearer ${token}`},
        })
        : await axios.default.post(`${baseUrl}v1/admin/deliverer`, payload, {
          headers: {Authorization: `Bearer ${token}`},
        });
      onSaved(response.data);
    } catch (e) {
      setError('Eroare la salvare.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="xs">
      <div className="deliverer-modal">
        <div className="deliverer-modal-topbar">
          <h3 className="deliverer-modal-title">{isEditing ? 'Editează livrator' : 'Adaugă livrator'}</h3>
          <IconButton className="deliverer-modal-close" size="small" onClick={onClose}>
            <CloseIcon/>
          </IconButton>
        </div>

        <div className="deliverer-modal-fields">
          <TextField
            label="Prenume"
            value={form.firstName}
            onChange={setField('firstName')}
            fullWidth
            margin="dense"
          />
          <TextField
            label="Nume"
            value={form.lastName}
            onChange={setField('lastName')}
            fullWidth
            margin="dense"
          />
          <TextField
            label="Telefon"
            value={form.phone}
            onChange={setField('phone')}
            fullWidth
            margin="dense"
          />
          <TextField
            select
            label="Oraș"
            value={form.cityId}
            onChange={setField('cityId')}
            fullWidth
            margin="dense"
          >
            <MenuItem value="">Alege oraș</MenuItem>
            {cities.map((city) => (
              <MenuItem key={city.id} value={city.id}>{city.name}</MenuItem>
            ))}
          </TextField>
          <TextField
            label="Username"
            value={form.username}
            onChange={setField('username')}
            fullWidth
            margin="dense"
          />
          <TextField
            label="Parolă"
            type={showPassword ? 'text' : 'password'}
            value={form.password}
            onChange={setField('password')}
            fullWidth
            margin="dense"
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton size="small" onClick={() => setShowPassword(!showPassword)} tabIndex={-1}>
                    {showPassword ? <VisibilityOff fontSize="small"/> : <Visibility fontSize="small"/>}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
          <FormControlLabel
            control={
              <Switch
                checked={form.platitPeComanda}
                onChange={(event) => setForm({...form, platitPeComanda: event.target.checked})}
              />
            }
            label="Plătit pe comandă"
          />
          {form.platitPeComanda && (
            <TextField
              label="Sumă (lei/comandă)"
              type="number"
              value={form.sumaComanda}
              onChange={setField('sumaComanda')}
              fullWidth
              margin="dense"
            />
          )}
          {isSuperAdmin && (
            <div style={{display: 'flex', alignItems: 'center'}}>
              <TextField
                label="Salariu de bază"
                type="number"
                value={baseSalary}
                onChange={(event) => setBaseSalary(event.target.value)}
                fullWidth
                margin="dense"
              />
              {isEditing && (
                <IconButton
                  size="small"
                  title="Istoric salariu de bază"
                  onClick={(event) => setSalaryHistoryAnchor(event.currentTarget)}
                >
                  <HistoryIcon fontSize="small"/>
                </IconButton>
              )}
            </div>
          )}
        </div>

        {error && <div className="deliverer-modal-error">{error}</div>}

        <div className="deliverer-modal-footer">
          <Button onClick={onClose} disabled={saving}>Anulează</Button>
          <Button onClick={save} disabled={saving} variant="contained" color="primary">
            Salvează
          </Button>
        </div>
      </div>

      <Menu
        anchorEl={salaryHistoryAnchor}
        open={!!salaryHistoryAnchor}
        onClose={() => setSalaryHistoryAnchor(null)}
      >
        <div className="deliverers-status-menu-header">Istoric salariu de bază</div>
        {baseSalaryHistory.length === 0 ? (
          <MenuItem disabled>Nicio schimbare înregistrată</MenuItem>
        ) : baseSalaryHistory.slice().reverse().map((change) => (
          <MenuItem key={change.id} disabled>
            {formatDate(new Date(change.effective_from))} — {change.base_salary} lei
          </MenuItem>
        ))}
      </Menu>
    </Dialog>
  );
}
